import Spinner from "components/SimpleComponents/Spinner/Spinner";
import { useSettings } from "contexts/SettingsManager/SettingsManager";

import styled, {
  FlattenSimpleInterpolation,
  css,
} from "styled-components/macro";
import {
  FONT_SIZE_EXTRA_SMALL,
  FONT_SIZE_MEDIUM,
  getIsIe11,
} from "constants/constants";
import { getIsFirefox } from "utils/cssUtils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

export const getExpandedTileContentHeights = (compact = false) => ({
  singleLine: { sm: compact ? 27 : 36, lg: 40 },
  doubleLine: { sm: 41, lg: 82 },
  operatorName: { sm: 36, lg: 72 },
  teamSize: { sm: 36, lg: 72 },
  tileButterfly: { sm: 16, lg: 32 },
  tileAheadBehind: { sm: 66, lg: 146 },
  topLosses: { sm: 124, lg: 248 },
});

/** maximum rows to display in the expanded tile for skuName */
const MAX_SKU_NAME_ROWS = 2;

/** an Expanded Tile module containing one line of text (bold) */
export const SingleLineModule = ({
  data,
  loading,
  css = "",
  showThisShiftIndicator = false,
}: {
  data: any;
  loading: boolean;
  css?: string;
  showThisShiftIndicator?: boolean;
}) => {
  const { isLargeTiles, tileScaleMultiplier } = useSettings();
  const [
    {
      general: { expandedProcessTileCompactMode },
    },
  ] = useFlightConfig();
  return (
    <SingleLineModuleStyles
      {...{
        $customCss: css,
        $isLargeTiles: isLargeTiles,
        $isIE: getIsIe11(),
        $showThisShiftIndicator: showThisShiftIndicator,
        $compact: expandedProcessTileCompactMode,
        $tileScaleMultiplier: tileScaleMultiplier ** 0.45,
      }}
    >
      {loading ? <Spinner width={10} /> : <div className="line-1">{data}</div>}
    </SingleLineModuleStyles>
  );
};

const SingleLineModuleStyles = styled.div<{
  $isLargeTiles: boolean;
  $isIE: boolean;
  $showThisShiftIndicator?: boolean;
  $customCss: string | FlattenSimpleInterpolation;
  $compact: boolean;
  $tileScaleMultiplier: number;
}>`
  height: ${props =>
    getExpandedTileContentHeights(props.$compact).singleLine[
      props.$isLargeTiles ? "lg" : "sm"
    ]}px;
  font-size: ${props =>
    (props.$isLargeTiles ? FONT_SIZE_MEDIUM : FONT_SIZE_EXTRA_SMALL) *
    props.$tileScaleMultiplier}px;
  font-weight: bold;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  .line-1 {
    /* on ie11, only show two rows, no ellipsis */
    ${props =>
      props.$isIE ? "flex-wrap: wrap; width: 100%; max-height: 2em;" : ""}
    display: flex;
    width: 100%;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    text-align: center;
    /* hide overflow with ellipsis after 2 lines https://css-tricks.com/line-clampin/ */
    display: -webkit-box;
    -webkit-line-clamp: ${MAX_SKU_NAME_ROWS};
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.2em;
  }
  position: relative;
  &:after {
    content: ${p => (p.$showThisShiftIndicator ? `"(shift)"` : `""`)};
    position: absolute;
    opacity: 0.5;
    font-size: ${p => 10}px;
    left: ${p => 12}px;
    bottom: ${p => 2}px;
  }
  @media print {
    &:after {
      display: none;
    }
  }
  ${props => props.$customCss}
`;

/** an Expanded Tile module containing two lines of text (top line is bold) */
export const DoubleLineModule = ({
  dataLine1,
  dataLine2,
  loading,
  css = "",
}: {
  dataLine1: any;
  dataLine2: any;
  loading: boolean;
  css?: string;
}) => {
  const { isLargeTiles, tileScaleMultiplier } = useSettings();
  const [
    {
      general: { expandedProcessTileCompactMode },
    },
  ] = useFlightConfig();
  return (
    <DoubleLineModuleStyles
      {...{
        $customCss: css,
        $isLargeTiles: isLargeTiles,
        $isIE: getIsIe11(),
        $compact: expandedProcessTileCompactMode,
        $tileScaleMultiplier: tileScaleMultiplier ** 0.45,
      }}
    >
      {loading ? (
        <Spinner width={10} />
      ) : (
        <>
          <div className="line-1">{dataLine1}</div>
          <div className="line-2">{dataLine2}</div>
        </>
      )}
    </DoubleLineModuleStyles>
  );
};

const DoubleLineModuleStyles = styled.div<{
  $isLargeTiles: boolean;
  $isIE: boolean;
  $customCss: string | FlattenSimpleInterpolation;
  $compact: boolean;
  $tileScaleMultiplier: number;
}>`
  height: ${props =>
    getExpandedTileContentHeights(props.$compact).doubleLine[
      props.$isLargeTiles ? "lg" : "sm"
    ]}px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  text-align: center;
  font-size: ${props =>
    (props.$isLargeTiles ? FONT_SIZE_MEDIUM : FONT_SIZE_EXTRA_SMALL) *
    props.$tileScaleMultiplier}px;
  .line-1,
  .line-2 {
    /* on ie11, only show two rows, no ellipsis */
    ${props =>
      props.$isIE
        ? css`
            flex-wrap: wrap;
            width: 100%;
            max-height: 2em;
          `
        : ""}
    max-width: 100%;
    display: flex;
    justify-content: ${getIsFirefox() ? "flex-start" : "center"};
    flex-direction: column;
    align-items: center;
    text-align: center;
    /* hide overflow with ellipsis after 2 lines https://css-tricks.com/line-clampin/ */
    display: -webkit-box;
    -webkit-line-clamp: ${MAX_SKU_NAME_ROWS};
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.2em;
  }
  .line-1 {
    font-weight: bold;
    max-height: 1em;
  }
  .line-2 {
    height: fit-content;
  }

  ${props => props.$customCss}
`;
